import React, { useState, useCallback } from 'react';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';
import { supabase } from './lib/supabaseClient';
import Sidebar from './components/Sidebar';
import MigrationWizard from './components/MigrationWizard';
import DashboardPage from './pages/DashboardPage';
import GeneratePage from './pages/GeneratePage';
import FavoritesPage from './pages/FavoritesPage';
import TemplatesPage from './pages/TemplatesPage';
import SettingsPage from './pages/SettingsPage';
import TranslatePage from './pages/TranslatePage';
import VocabularyPage from './pages/VocabularyPage';
import LogsPage from './pages/LogsPage';
import AdminPage from './pages/AdminPage';
import AuthPage from './pages/AuthPage';
import PendingApprovalPage from './pages/PendingApprovalPage';
import AccountDisabledPage from './pages/AccountDisabledPage';
import NewsletterPage from './pages/NewsletterPage';
import BulkGenerator from './BulkGenerator';
import AutopilotPage from './pages/AutopilotPage';
import ImageManagerPage from './pages/ImageManagerPage';
import ComingSoonPage from './pages/ComingSoonPage';
import MetaStatsPage from './pages/MetaStatsPage';
import './App.css';

// Market code (SE/NO/…) → full display name used in GeneratePage selects
const MARKET_CODE_TO_DISPLAY = {
  SE: 'Sverige', NO: 'Norge', FI: 'Finland', DE: 'Tyskland', DK: 'Danmark', NL: 'Nederländerna',
};

function AppShell() {
  const [page, setPage] = useState('dashboard');
  const [generatePrefill, setGeneratePrefill] = useState(null);
  const {
    user, loading, isPasswordRecovery,
    profile, profileLoading,
    isAdmin, isPending, isInactive, isActive,
  } = useAuth() || {};

  // Called by FavoritesPage when user clicks "Generera liknande"
  const handleGenerateSimilar = useCallback((fav) => {
    setGeneratePrefill({
      productName: fav.productName || '',
      category:    fav.category    || '',
      market:      MARKET_CODE_TO_DISPLAY[fav.market] || fav.market || '',
      features:    fav.features    || '',
    });
    setPage('generate');
  }, []);

  // Password recovery flow — show set-new-password screen
  if (supabase && isPasswordRecovery) {
    return <AuthPage initialMode="recovery" />;
  }

  // If Supabase is configured and user is not logged in, show auth screen
  if (supabase && !loading && !user) {
    return <AuthPage />;
  }

  // While Supabase session is loading, show nothing (prevents flash)
  if (supabase && loading) {
    return (
      <div className="auth-screen">
        <div className="auth-loading">
          <div className="auth-loading-spinner" />
        </div>
      </div>
    );
  }

  // Profile-status gating (V12 user_profiles)
  if (supabase && user && !profile && profileLoading) {
    return (
      <div className="auth-screen">
        <div className="auth-loading">
          <div className="auth-loading-spinner" />
        </div>
      </div>
    );
  }
  if (supabase && user && isPending) {
    return <PendingApprovalPage email={user.email} />;
  }
  if (supabase && user && isInactive) {
    return <AccountDisabledPage email={user.email} />;
  }

  // If profile loaded but status is unrecognized, treat as pending (defensive)
  if (supabase && user && profile && !isActive && !isPending && !isInactive) {
    return <PendingApprovalPage email={user.email} />;
  }

  return (
    <div className="app-shell">
      <Toaster position="top-right" richColors />
      <MigrationWizard />
      <Sidebar activePage={page} onNavigate={setPage} user={user} isAdmin={isAdmin} />
      <main className="content-area">
        {page === 'dashboard'  && <DashboardPage key={Date.now()} />}
        {page === 'generate'   && <GeneratePage prefill={generatePrefill} onPrefillUsed={() => setGeneratePrefill(null)} />}
        {page === 'bulk'       && (
          <div className="page-scroll">
            <div className="page-header">
              <h1 className="page-title">Massimport</h1>
              <p className="page-subtitle">Ladda upp Excel eller CSV — appen genererar en beskrivning per rad.</p>
            </div>
            <BulkGenerator />
          </div>
        )}
        {page === 'templates'  && <TemplatesPage />}
        {page === 'autopilot'  && <AutopilotPage />}
        {page === 'images'     && <ImageManagerPage />}
        {page === 'newsletter' && <NewsletterPage />}
        {page === 'translate'  && <TranslatePage />}
        {page === 'vocabulary' && <VocabularyPage />}
        {page === 'favorites'  && <FavoritesPage onGenerateSimilar={handleGenerateSimilar} />}
        {page === 'admin'      && (isAdmin ? <AdminPage /> : <DashboardPage key={Date.now()} />)}
        {page === 'logs'       && <LogsPage />}
        {page === 'settings'   && <SettingsPage />}
        {page === 'metastats'  && <MetaStatsPage />}
        {/* Admin-only "Snart"-platshållare (vanliga användare ser låst sidebar) */}
        {page === 'storyblok'  && (isAdmin ? <ComingSoonPage title="Storyblok" description="Synka och hantera innehåll mot Storyblok CMS." /> : <DashboardPage key={Date.now()} />)}
        {page === 'scheduler'  && (isAdmin ? <ComingSoonPage title="Schemaläggare" description="Planera och automatisera körningar av Autopilot." /> : <DashboardPage key={Date.now()} />)}
        {page === 'marketplan' && (isAdmin ? <ComingSoonPage title="Marknadsplan" description="Översikt och planering av marknadsaktiviteter." /> : <DashboardPage key={Date.now()} />)}
      </main>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <DataProvider>
        <AppShell />
      </DataProvider>
    </AuthProvider>
  );
}
